import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot, faBookOpen, faGraduationCap, faArrowRight, faClock,
  faLayerGroup, faCloud, faShieldHalved, faListCheck, faWandMagicSparkles,
  faCircleCheck, faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import SEO from '../components/SEO';
import LocalizedLink from '../components/LocalizedLink';
import { AI_COURSES, STUDY_GUIDES, type LearningTrack } from '../data/learningContent';

const trackIcon: Record<LearningTrack, typeof faRobot> = {
  ai: faRobot,
  cloud: faCloud,
  security: faShieldHalved,
  project: faListCheck,
  general: faBookOpen,
};

const levelLabelKey = {
  beginner: 'learn.level.beginner',
  intermediate: 'learn.level.intermediate',
  advanced: 'learn.level.advanced',
} as const;

const Learn: React.FC = () => {
  const { t } = useTranslation();

  const courseListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: t('learn.seo.title'),
    description: t('learn.seo.description'),
    itemListElement: AI_COURSES.map((course, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Course',
        name: t(course.titleKey),
        description: t(course.descriptionKey),
        provider: { '@type': 'Organization', name: 'GlobalPreps', url: 'https://globalpreps.com' },
        educationalLevel: course.level,
        timeRequired: `PT${course.durationHours}H`,
        inLanguage: 'en',
        courseMode: 'online',
      },
    })),
  };

  return (
    <>
      <SEO
        title={t('learn.seo.title')}
        description={t('learn.seo.description')}
        keywords={t('learn.seo.keywords')}
        structuredData={courseListSchema}
        breadcrumbs={[
          { name: t('nav.home'), url: '/' },
          { name: t('nav.learn'), url: '/learn' },
        ]}
      />

      <div className="gp-page">
        <section className="gp-container pt-6 sm:pt-8 pb-8" aria-labelledby="learn-hero-heading">
          <span className="gp-badge gp-badge-ai mb-3">
            <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden="true" />
            {t('learn.badge')}
          </span>
          <h1 id="learn-hero-heading" className="gp-heading-xl max-w-2xl mb-2">
            {t('learn.heroTitle')}
          </h1>
          <p className="text-sm sm:text-base gp-text-muted max-w-xl mb-5">{t('learn.heroSubtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <a href="#ai-courses" className="gp-btn-primary">
              {t('learn.exploreAiCourses')}
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
            <LocalizedLink to="/certifications" className="gp-btn-ghost">
              <FontAwesomeIcon icon={faGraduationCap} aria-hidden="true" />
              {t('learn.browseCertifications')}
            </LocalizedLink>
          </div>
        </section>

        <section className="gp-container gp-section" aria-labelledby="learn-path-heading">
          <h2 id="learn-path-heading" className="gp-heading-lg mb-4">{t('learn.pathTitle')}</h2>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 list-none p-0 m-0">
            {(['learn', 'practice', 'certify'] as const).map((step, i) => (
              <li key={step} className="gp-card relative pt-5">
                <span
                  className="absolute -top-2.5 left-4 w-7 h-7 rounded-md text-xs font-bold flex items-center justify-center text-white"
                  style={{ background: 'var(--gp-brand)' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <h3 className="font-display text-base font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                  {t(`learn.path.${step}.title`)}
                </h3>
                <p className="text-sm gp-text-muted">{t(`learn.path.${step}.description`)}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="ai-courses" className="gp-container gp-section scroll-mt-20" aria-labelledby="ai-courses-heading">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
            <div>
              <h2 id="ai-courses-heading" className="gp-heading-lg flex items-center gap-2">
                <FontAwesomeIcon icon={faRobot} className="text-lg" style={{ color: 'var(--gp-ai)' }} aria-hidden="true" />
                {t('learn.aiCoursesTitle')}
              </h2>
              <p className="gp-text-muted mt-1 text-sm max-w-xl">{t('learn.aiCoursesSubtitle')}</p>
            </div>
            <p
              className="text-xs font-medium px-3 py-1.5 rounded-md max-w-xs"
              style={{ background: 'var(--gp-accent-soft)', color: 'var(--gp-accent)' }}
            >
              {t('learn.comingSoonNote')}
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 list-none p-0 m-0">
            {AI_COURSES.map((course) => {
              const TrackIcon = trackIcon[course.track];
              const available = course.status === 'available';
              const CardInner = (
                <article className={`gp-card h-full flex flex-col ${available ? 'gp-card-interactive' : ''}`}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div
                      className="w-9 h-9 rounded-md flex items-center justify-center"
                      style={{ background: 'var(--gp-ai-soft)' }}
                    >
                      <FontAwesomeIcon icon={TrackIcon} className="text-sm" style={{ color: 'var(--gp-ai)' }} aria-hidden="true" />
                    </div>
                    <span
                      className="text-[11px] font-bold px-1.5 py-0.5 rounded"
                      style={{
                        background: available ? 'var(--gp-success-soft)' : 'var(--gp-surface-muted)',
                        color: available ? 'var(--gp-success)' : 'var(--gp-ink-muted)',
                      }}
                    >
                      {available ? t('learn.status.available') : t('learn.status.comingSoon')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                    {t(course.titleKey)}
                  </h3>
                  <p className="text-sm gp-text-muted flex-1 mb-3">{t(course.descriptionKey)}</p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {course.topics.map((topic) => (
                      <span key={topic} className="text-[11px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'var(--gp-brand-soft)', color: 'var(--gp-brand)' }}>
                        {topic}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t text-xs gp-text-muted" style={{ borderColor: 'var(--gp-border)' }}>
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                      {t('learn.durationHours', { count: course.durationHours })}
                    </span>
                    {available ? (
                      <span className="font-semibold inline-flex items-center gap-1" style={{ color: 'var(--gp-brand)' }}>
                        {t('learn.openCourse')}
                        <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" aria-hidden="true" />
                      </span>
                    ) : (
                      <span className="font-semibold" style={{ color: 'var(--gp-brand)' }}>
                        {t(levelLabelKey[course.level])}
                      </span>
                    )}
                  </div>
                </article>
              );

              return (
                <li key={course.id}>
                  {available ? (
                    <LocalizedLink to={`/learn/${course.slug}`} className="block no-underline h-full">
                      {CardInner}
                    </LocalizedLink>
                  ) : (
                    CardInner
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section id="study-guides" className="gp-container gp-section scroll-mt-20" aria-labelledby="study-guides-heading">
          <h2 id="study-guides-heading" className="gp-heading-lg flex items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faBookOpen} className="text-lg" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
            {t('learn.studyGuidesTitle')}
          </h2>
          <p className="gp-text-muted mb-4 text-sm max-w-xl">{t('learn.studyGuidesSubtitle')}</p>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 list-none p-0 m-0">
            {STUDY_GUIDES.map((guide) => {
              const TrackIcon = trackIcon[guide.track];
              return (
                <li key={guide.id}>
                  <article className="gp-card gp-card-interactive flex gap-3">
                    <div
                      className="w-9 h-9 shrink-0 rounded-md flex items-center justify-center"
                      style={{ background: 'var(--gp-brand-soft)' }}
                    >
                      <FontAwesomeIcon icon={TrackIcon} className="text-sm" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm mb-0.5" style={{ color: 'var(--gp-ink)' }}>{t(guide.titleKey)}</h3>
                      <p className="text-xs gp-text-muted mb-2">{t(guide.descriptionKey)}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="gp-text-muted flex items-center gap-1">
                          <FontAwesomeIcon icon={faHourglassHalf} aria-hidden="true" />
                          {t('learn.readMinutes', { count: guide.readMinutes })}
                        </span>
                        <LocalizedLink
                          to="/certifications"
                          className="font-semibold inline-flex items-center gap-1 hover:underline"
                          style={{ color: 'var(--gp-brand)' }}
                        >
                          {t('learn.startPracticing')}
                          <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" aria-hidden="true" />
                        </LocalizedLink>
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="gp-container pb-10 sm:pb-12" aria-labelledby="practice-cta-heading">
          <div
            className="gp-cta-band flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            style={{ background: 'linear-gradient(135deg, var(--gp-brand) 0%, #0a3d42 100%)', color: '#fff' }}
          >
            <div>
              <h2 id="practice-cta-heading" className="font-display text-xl font-semibold mb-1 flex items-center gap-2">
                <FontAwesomeIcon icon={faLayerGroup} aria-hidden="true" />
                {t('learn.practiceCtaTitle')}
              </h2>
              <p className="opacity-90 text-sm max-w-lg mb-3">{t('learn.practiceCtaSubtitle')}</p>
              <ul className="space-y-1.5 text-xs opacity-90">
                {[t('learn.practicePoint1'), t('learn.practicePoint2'), t('learn.practicePoint3')].map((point) => (
                  <li key={point} className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <LocalizedLink
              to="/register"
              className="shrink-0 inline-flex items-center justify-center gap-2 font-semibold rounded-md px-5 py-2 text-sm bg-white transition-colors hover:bg-white/90"
              style={{ color: 'var(--gp-brand)' }}
            >
              {t('home.getStarted')}
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </LocalizedLink>
          </div>
        </section>
      </div>
    </>
  );
};

export default Learn;
